import clsx from 'clsx';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { addDays, addMonths, matchesSearch } from '@pz/core';
import { HoursLegend, hoursFill, hoursInk } from './analysis/common';
import { ProjectDot } from './entry';
import { Button, Checkbox, ErrorBox, Field, Input, Loading, Panel, Select, td, th } from './ui';
import { ScrollTable } from './virtual';
import { d, h } from '../lib/format';
import { useQ } from '../lib/hooks';
import { useMe } from '../lib/me';

/**
 * Wer arbeitet wo (request 21 of round 4, request 19 of round 5): projects in rows, people in columns; a cell shows the
 * booked hours of the person in the project in the period (blue ramp, relative to the largest cell, always with the
 * number), the project lead ("PL") and members without hours (ring). The view is a right ("staffing.view": employees,
 * office and the technical admin by default, externals not). Hours only where the viewer sees the hours of the project
 * anyway (project lead, office and management, "all projects"); elsewhere a filled box says "works in this project".
 * Filters: period, closed and archived projects (scrolling list), person, search. Full project names on the right.
 */

type Range = '4w' | '3m' | '12m';
const ROWS = 40;

function rangeOf(today: string, r: Range) {
  if (r === '4w') return { from: addDays(today, -27), to: today };
  if (r === '12m') return { from: addDays(addMonths(today, -12), 1), to: today };
  return { from: addDays(addMonths(today, -3), 1), to: today };
}

export function ProjectStaffing() {
  const { t } = useTranslation();
  const { me, can, isLead } = useMe();
  const allowed = can('staffing.view', 'projects.view.all', 'time.approve.final', 'time.manage', 'billing.manage', 'kpi.team.view') || isLead();
  const [range, setRange] = useState<Range>('3m');
  const [inactive, setInactive] = useState(false);
  const [archived, setArchived] = useState(false);
  const [person, setPerson] = useState('');
  const [search, setSearch] = useState('');
  const [onlyHours, setOnlyHours] = useState(false);
  const [rows, setRows] = useState(ROWS);
  const period = rangeOf(me.today, range);
  const q = useQ('projects.staffing', { ...period, includeInactive: inactive, includeArchived: archived }, { enabled: allowed });

  const view = useMemo(() => {
    const data = q.data;
    if (!data) return null;
    const counts = (c: { minutes: number | null; role: string | null }) => (onlyHours ? (c.minutes ?? 0) > 0 : (c.minutes ?? 0) > 0 || !!c.role);
    const byProject = new Map<string, typeof data.cells>();
    for (const c of data.cells) {
      if (!counts(c)) continue;
      const list = byProject.get(c.projectId) ?? [];
      list.push(c);
      byProject.set(c.projectId, list);
    }
    const projects = data.projects.filter(
      (p) => matchesSearch(`${p.number} ${p.name} ${p.customerName ?? ''}`, search) && (byProject.get(p.id)?.length ?? 0) > 0 && (!person || byProject.get(p.id)!.some((c) => c.personId === person)),
    );
    const shownIds = new Set(projects.map((p) => p.id));
    const minutesOf = new Map<string, number>();
    const inView = new Set<string>();
    for (const p of projects) {
      for (const c of byProject.get(p.id) ?? []) {
        inView.add(c.personId);
        minutesOf.set(c.personId, (minutesOf.get(c.personId) ?? 0) + (c.minutes ?? 0));
      }
    }
    const persons = data.persons.filter((x) => inView.has(x.id)).sort((a, b) => (minutesOf.get(b.id) ?? 0) - (minutesOf.get(a.id) ?? 0) || b.projects - a.projects || a.name.localeCompare(b.name));
    const cell = new Map(data.cells.filter((c) => shownIds.has(c.projectId) && counts(c)).map((c) => [`${c.projectId}|${c.personId}`, c]));
    const max = Math.max(1, ...[...cell.values()].map((c) => c.minutes ?? 0));
    const names = new Map(data.persons.map((p) => [p.id, p.name]));
    const leadsOf = (projectId: string) =>
      data.cells
        .filter((c) => c.projectId === projectId && c.role === 'lead')
        .map((c) => names.get(c.personId) ?? '')
        .filter(Boolean);
    return { projects, persons, cell, max, minutesOf, all: data.persons, leadsOf, hours: data.hours };
  }, [q.data, onlyHours, search, person]);

  if (!allowed) return null;
  return (
    <Panel title={t('staffing.title')} className="mt-6">
      <p className="mb-3 text-sm text-graphite">{q.data?.scope === 'lead' ? t('staffing.introLead') : q.data && !q.data.hours ? t('staffing.introBoxes') : t('staffing.intro')}</p>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Field label={t('staffing.period')} className="w-44">
          <Select className="h-8" value={range} onChange={(e) => setRange(e.target.value as Range)}>
            {(['4w', '3m', '12m'] as const).map((r) => (
              <option key={r} value={r}>
                {t(`staffing.ranges.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.person')} className="w-56">
          <Select className="h-8" value={person} onChange={(e) => setPerson(e.target.value)} search>
            <option value="">{t('common.all')}</option>
            {(view?.all ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.search')} className="w-56">
          <div className="relative">
            <Search className="pointer-events-none absolute top-2 left-2 size-4 text-faint" />
            <Input className="h-8 pl-7" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('staffing.searchProject')} />
          </div>
        </Field>
        {q.data?.hours && <Checkbox label={t('staffing.onlyHours')} checked={onlyHours} onChange={(e) => setOnlyHours(e.target.checked)} />}
        <Checkbox label={t('staffing.inactive')} checked={inactive} onChange={(e) => setInactive(e.target.checked)} />
        <Checkbox label={t('staffing.archived')} checked={archived} onChange={(e) => setArchived(e.target.checked)} />
        {q.data && <span className="num ml-auto mb-1.5 text-xs text-graphite">{t('kpi.asOf', { from: d(q.data.from), to: d(q.data.to) })}</span>}
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {view && view.projects.length === 0 && <p className="text-sm text-graphite">{t('common.noResults')}</p>}
      {view && view.projects.length > 0 && (
        <>
          <ScrollTable label={t('staffing.title')} maxHeight="min(70vh, 44rem)">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={clsx(th, 'sticky top-0 left-0 z-20 bg-sheet-2 align-bottom')}>{t('common.project')}</th>
                  {view.persons.map((p) => (
                    <th
                      key={p.id}
                      className="sticky top-0 z-10 bg-sheet-2 px-1 pb-2 align-bottom font-medium text-graphite"
                      title={`${p.name}${p.discipline ? ` · ${p.discipline}` : ''}${p.minutes !== null ? ` · ${h(view.minutesOf.get(p.id) ?? 0, 1)} h` : ''}`}
                    >
                      <span className="mx-auto block max-h-36 overflow-hidden text-xs whitespace-nowrap" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                        {p.name}
                      </span>
                    </th>
                  ))}
                  {view.hours && <th className={clsx(th, 'sticky top-0 z-10 bg-sheet-2 text-right align-bottom')}>{t('common.total')}</th>}
                  <th className={clsx(th, 'sticky top-0 z-10 w-full min-w-64 bg-sheet-2 align-bottom')}>{t('staffing.projectName')}</th>
                </tr>
              </thead>
              <tbody>
                {view.projects.slice(0, rows).map((p) => {
                  const sum = view.persons.reduce((s, x) => s + (view.cell.get(`${p.id}|${x.id}`)?.minutes ?? 0), 0);
                  const leads = view.leadsOf(p.id);
                  return (
                    <tr key={p.id} className="border-b border-line/60">
                      <td className={clsx(td, 'sticky left-0 z-10 bg-sheet whitespace-nowrap')}>
                        <span className="inline-flex items-center gap-1.5">
                          <ProjectDot color={p.color} />
                          <Link to={`/projects/${p.id}`} className="num font-semibold text-accent hover:underline" title={p.name}>
                            {p.number}
                          </Link>
                        </span>
                      </td>
                      {view.persons.map((x) => {
                        const c = view.cell.get(`${p.id}|${x.id}`);
                        if (!c) return <td key={x.id} className="p-0.5" />;
                        const minutes = c.minutes ?? 0;
                        const hidden = c.minutes === null;
                        const share = minutes / view.max;
                        const label = [
                          `${x.name} · ${p.number} ${p.name}`,
                          hidden ? t('staffing.cellWorks') : minutes > 0 ? t('staffing.cellHours', { hours: h(minutes, 1) }) : t('staffing.cellNoHours'),
                          c.role === 'lead' ? t('projects.role.lead') : c.role === 'member' ? t('projects.role.member') : t('staffing.noMember'),
                        ].join(' · ');
                        return (
                          <td key={x.id} className="p-0.5 text-center">
                            <span
                              title={label}
                              aria-label={label}
                              role="img"
                              className={clsx(
                                'num relative flex h-7 min-w-9 items-center justify-center rounded px-1 text-xs',
                                hidden ? 'bg-accent-soft text-accent' : minutes > 0 ? 'font-semibold' : 'border border-dashed border-line-strong text-graphite',
                              )}
                              style={!hidden && minutes > 0 ? { background: hoursFill(share), color: hoursInk(share) } : undefined}
                            >
                              {hidden ? <span aria-hidden className="size-2 rounded-full bg-current" /> : minutes > 0 ? h(minutes, 0) : '·'}
                              {c.role === 'lead' && <span className="absolute -top-1 -right-1 rounded bg-ink px-0.5 text-[9px] leading-tight font-semibold text-sheet">PL</span>}
                            </span>
                          </td>
                        );
                      })}
                      {view.hours && <td className={clsx(td, 'num text-right font-semibold')}>{p.hours ? h(sum, 0) : '–'}</td>}
                      <td className={clsx(td, 'min-w-64')}>
                        <span className="block font-medium">{p.name}</span>
                        <span className="block text-xs text-graphite">
                          {[p.customerName, leads.length ? `PL: ${leads.join(', ')}` : null, p.archived ? t('staffing.archivedTag') : p.status !== 'active' ? t(`projects.statuses.${p.status as 'active'}`) : null].filter(Boolean).join(' · ')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollTable>
          {view.projects.length > rows && (
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => setRows((r) => r + ROWS)}>
              {t('analysis.showMore', { count: view.projects.length - rows })}
            </Button>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-graphite">
            {view.hours && <HoursLegend />}
            {view.hours && (
              <span className="flex items-center gap-1">
                <span className="inline-block h-3 w-4 rounded-[3px] border border-dashed border-line-strong" />
                {t('staffing.legendMember')}
              </span>
            )}
            <span className="flex items-center gap-1">
              <span className="inline-flex h-3 w-4 items-center justify-center rounded-[3px] bg-accent-soft text-accent">
                <span className="size-1.5 rounded-full bg-current" />
              </span>
              {t('staffing.legendWorks')}
            </span>
            <span className="flex items-center gap-1">
              <span className="rounded bg-ink px-0.5 text-[9px] font-semibold text-sheet">PL</span>
              {t('projects.role.lead')}
            </span>
          </div>
          <p className="mt-1 text-xs text-graphite">{t('staffing.hint')}</p>
        </>
      )}
    </Panel>
  );
}
